<script lang="ts" setup>
import { ref, computed, toRef } from "vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import SectionHeader from "@/components/SectionHeader.vue";
import ScheduleCard from "./ScheduleCard.vue";
import {
  useSchedules,
  useDeleteSchedule,
  usePauseSchedule,
  useResumeSchedule,
  useRunScheduleNow,
} from "@/queries/schedules";
import { useProjects } from "@/queries/projects";
import { useAlertStore } from "@/stores/alerts-store";
import type { Schedule } from "@/types/schedule-types";

const alertStore = useAlertStore();

/** Run a schedule action, surfacing any failure as an error alert (one-shot list actions own their errors). */
async function withErrorAlert(title: string, fallback: string, action: () => Promise<unknown>) {
  try {
    await action();
  } catch (e) {
    alertStore.addError(title, e instanceof Error ? e.message : fallback);
  }
}

const props = withDefaults(
  defineProps<{
    /** When set, the list is scoped to this project (and the project column is hidden). */
    projectId?: string | null;
    /** Show the Total/Active/Paused/Failed summary cards (root page only). */
    showStats?: boolean;
    /** Show the state filter tabs + search row (root page only). */
    showFilters?: boolean;
    /** Show multi-select + bulk pause/resume/delete (root page only). */
    showBulk?: boolean;
    /** Heading shown above the list. */
    title?: string;
  }>(),
  {
    projectId: null,
    showStats: false,
    showFilters: false,
    showBulk: false,
    title: "Scheduled Workflows",
  }
);

const emit = defineEmits<{
  edit: [scheduleId: string];
  create: [];
}>();

const projectIdRef = toRef(props, "projectId");
const { schedules, schedulesLoading } = useSchedules(projectIdRef);
const { deleteScheduleAsync } = useDeleteSchedule();
const { pauseScheduleAsync } = usePauseSchedule();
const { resumeScheduleAsync } = useResumeSchedule();
const { runScheduleNowAsync } = useRunScheduleNow();

// Resolve projectId → name for the global list, where a schedule's project isn't otherwise visible.
// The project name is only shown when not scoped to a single project (i.e. the root Scheduler page);
// the projects query is app-cached so this is cheap. In the per-project tab the project is implied.
const isGlobal = computed(() => !props.projectId);
const { projects } = useProjects();
const projectNameById = computed(() => new Map(projects.value.map((p) => [p.id, p.name])));
function projectNameFor(schedule: Schedule): string | undefined {
  return isGlobal.value ? projectNameById.value.get(schedule.projectId) : undefined;
}

const searchQuery = ref("");
const selectedFilter = ref("all");
const selectedSchedules = ref<string[]>([]);
const showDeleteModal = ref(false);
const deleteModalMessage = ref("");
const pendingDeleteFn = ref<(() => void | Promise<unknown>) | null>(null);

const filterOptions = [
  { value: "all", label: "All", count: computed(() => schedules.value.length) },
  { value: "active", label: "Active", count: computed(() => schedules.value.filter((s) => s.status === "Active").length) },
  { value: "paused", label: "Paused", count: computed(() => schedules.value.filter((s) => s.status === "Paused").length) },
  { value: "inactive", label: "Inactive", count: computed(() => schedules.value.filter((s) => s.status === "Inactive" || s.status === "Completed" || s.status === "Expired").length) },
  { value: "failed", label: "Failed", count: computed(() => schedules.value.filter((s) => s.lastRunStatus === "Failed").length) },
];

const filteredSchedules = computed<Schedule[]>(() => {
  let filtered = schedules.value;

  if (props.showFilters && searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.cronExpression.toLowerCase().includes(q) ||
        (s.workflowName?.toLowerCase().includes(q) ?? false) ||
        (projectNameFor(s)?.toLowerCase().includes(q) ?? false)
    );
  }

  if (!props.showFilters) return filtered;

  switch (selectedFilter.value) {
    case "active":
      return filtered.filter((s) => s.status === "Active");
    case "paused":
      return filtered.filter((s) => s.status === "Paused");
    case "inactive":
      return filtered.filter((s) => s.status === "Inactive" || s.status === "Completed" || s.status === "Expired");
    case "failed":
      return filtered.filter((s) => s.lastRunStatus === "Failed");
    default:
      return filtered;
  }
});

const statsData = computed(() => ({
  total: schedules.value.length,
  active: schedules.value.filter((s) => s.status === "Active").length,
  paused: schedules.value.filter((s) => s.status === "Paused").length,
  failed: schedules.value.filter((s) => s.lastRunStatus === "Failed").length,
}));

function toggleScheduleSelection(scheduleId: string) {
  const i = selectedSchedules.value.indexOf(scheduleId);
  if (i > -1) selectedSchedules.value.splice(i, 1);
  else selectedSchedules.value.push(scheduleId);
}

function selectAllSchedules() {
  if (selectedSchedules.value.length === filteredSchedules.value.length) selectedSchedules.value = [];
  else selectedSchedules.value = filteredSchedules.value.map((s) => s.id);
}

async function bulkPause() {
  await withErrorAlert("Pause failed", "Unable to pause the selected schedules.", () =>
    Promise.all(selectedSchedules.value.map((id) => pauseScheduleAsync(id)))
  );
  selectedSchedules.value = [];
}
async function bulkResume() {
  await withErrorAlert("Resume failed", "Unable to resume the selected schedules.", () =>
    Promise.all(selectedSchedules.value.map((id) => resumeScheduleAsync(id)))
  );
  selectedSchedules.value = [];
}
function bulkDelete() {
  const ids = [...selectedSchedules.value];
  deleteModalMessage.value = `Are you sure you want to delete ${ids.length} selected schedule(s)?`;
  pendingDeleteFn.value = async () => {
    await withErrorAlert("Delete failed", "Unable to delete the selected schedules.", () =>
      Promise.all(ids.map((id) => deleteScheduleAsync(id)))
    );
    selectedSchedules.value = [];
  };
  showDeleteModal.value = true;
}

async function confirmDelete() {
  if (pendingDeleteFn.value) {
    await pendingDeleteFn.value();
    pendingDeleteFn.value = null;
  }
  showDeleteModal.value = false;
}

async function handleScheduleAction(action: string, scheduleId: string) {
  const schedule = schedules.value.find((s) => s.id === scheduleId);
  if (!schedule) return;

  switch (action) {
    case "toggle":
      await withErrorAlert("Action failed", "Unable to update the schedule.", () =>
        schedule.status === "Paused" ? resumeScheduleAsync(scheduleId) : pauseScheduleAsync(scheduleId)
      );
      break;
    case "run-now":
      await withErrorAlert("Run failed", "Unable to run the schedule now.", () => runScheduleNowAsync(scheduleId));
      break;
    case "edit":
      emit("edit", scheduleId);
      break;
    case "delete":
      deleteModalMessage.value = `Are you sure you want to delete "${schedule.name}"?`;
      pendingDeleteFn.value = () =>
        withErrorAlert("Delete failed", "Unable to delete the schedule.", () => deleteScheduleAsync(scheduleId));
      showDeleteModal.value = true;
      break;
  }
}
</script>

<template>
  <div class="d-flex flex-column gap-4">
    <!-- Quick Stats -->
    <PanelCard v-if="showStats">
      <SectionHeader title="Schedule Overview" />
      <div class="row g-3">
        <div class="col-md-3 col-6">
          <div class="card text-center"><div class="card-body">
            <div class="fs-3 fw-bold text-primary mb-2">{{ statsData.total }}</div>
            <div class="text-muted small">Total Schedules</div>
          </div></div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card text-center"><div class="card-body">
            <div class="fs-3 fw-bold text-success mb-2">{{ statsData.active }}</div>
            <div class="text-muted small">Active</div>
          </div></div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card text-center"><div class="card-body">
            <div class="fs-3 fw-bold text-warning mb-2">{{ statsData.paused }}</div>
            <div class="text-muted small">Paused</div>
          </div></div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card text-center"><div class="card-body">
            <div class="fs-3 fw-bold text-danger mb-2">{{ statsData.failed }}</div>
            <div class="text-muted small">Failed</div>
          </div></div>
        </div>
      </div>
    </PanelCard>

    <!-- Filters and Search -->
    <PanelCard v-if="showFilters">
      <SectionHeader title="Filters & Search" />
      <div class="row g-3 align-items-md-center">
        <div class="col-md-4">
          <div class="search-input-wrapper">
            <i class="bi bi-search search-icon"></i>
            <input type="text" class="form-control ps-5" placeholder="Search schedules..." v-model="searchQuery" />
          </div>
        </div>
        <div class="col-md-8">
          <div class="filter-tabs d-flex gap-2 flex-wrap">
            <button
              v-for="filter in filterOptions"
              :key="filter.value"
              class="filter-tab d-flex align-items-center gap-2"
              :class="{ active: selectedFilter === filter.value }"
              @click="selectedFilter = filter.value"
            >
              {{ filter.label }}
              <span class="badge">{{ filter.count.value }}</span>
            </button>
          </div>
        </div>
      </div>
    </PanelCard>

    <!-- Bulk Actions -->
    <div v-if="showBulk && selectedSchedules.length > 0" class="bulk-actions">
      <div class="d-flex align-items-center gap-3">
        <span class="text-muted">{{ selectedSchedules.length }} selected</span>
        <div class="d-flex gap-2">
          <button class="bb-btn-secondary-cta btn-sm" @click="bulkPause"><i class="bi bi-pause me-1"></i>Pause</button>
          <button class="bb-btn-secondary-cta btn-sm" @click="bulkResume"><i class="bi bi-play me-1"></i>Resume</button>
          <button class="bb-btn-danger btn-sm" @click="bulkDelete"><i class="bi bi-trash me-1"></i>Delete</button>
        </div>
        <button class="bb-btn-secondary-cta btn-sm ms-auto" @click="selectedSchedules = []">Clear Selection</button>
      </div>
    </div>

    <!-- Schedule List -->
    <PanelCard>
      <SectionHeader :title="`${title} (${filteredSchedules.length})`" />

      <div v-if="showBulk" class="list-header mb-3">
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            :checked="selectedSchedules.length === filteredSchedules.length && filteredSchedules.length > 0"
            :indeterminate="selectedSchedules.length > 0 && selectedSchedules.length < filteredSchedules.length"
            @change="selectAllSchedules"
          />
          <label class="form-check-label">Select All ({{ filteredSchedules.length }} schedules)</label>
        </div>
      </div>

      <div class="schedules-grid">
        <ScheduleCard
          v-for="schedule in filteredSchedules"
          :key="schedule.id"
          :schedule="schedule"
          :workflow-name="schedule.workflowName ?? undefined"
          :project-name="projectNameFor(schedule)"
          :selected="selectedSchedules.includes(schedule.id)"
          :selectable="showBulk"
          @toggle-selection="toggleScheduleSelection"
          @action="handleScheduleAction"
          @edit="(id: string) => emit('edit', id)"
        />
      </div>

      <!-- Loading -->
      <div v-if="schedulesLoading && filteredSchedules.length === 0" class="empty-state">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="text-muted mt-3">Loading schedules…</p>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredSchedules.length === 0" class="empty-state">
        <i class="bi bi-calendar-x display-1 text-muted"></i>
        <h3 class="mt-3 text-muted">No Schedules Found</h3>
        <p class="text-muted">
          <span v-if="showFilters && searchQuery">No schedules match your search criteria.</span>
          <span v-else-if="showFilters && selectedFilter !== 'all'">No schedules found for the selected filter.</span>
          <span v-else-if="projectId">This project has no schedules yet.</span>
          <span v-else>You haven't created any scheduled workflows yet.</span>
        </p>
        <button class="bb-btn-primary-cta" @click="emit('create')">
          <i class="bi bi-plus me-1"></i>{{ projectId ? "New Schedule" : "Create Your First Schedule" }}
        </button>
      </div>
    </PanelCard>

    <!-- Delete Confirmation Modal -->
    <div
      class="modal fade"
      :class="{ show: showDeleteModal }"
      :style="{ display: showDeleteModal ? 'block' : 'none' }"
      @click.self="showDeleteModal = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-danger"><i class="bi bi-exclamation-triangle me-2"></i>Delete Schedule</h2>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p>{{ deleteModalMessage }}</p>
            <p class="text-muted mb-0">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showDeleteModal = false">Cancel</button>
            <button class="btn btn-danger" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>
  </div>
</template>

<style lang="scss" scoped>
.filter-tab {
  border: 1px solid var(--bs-border-color);
  background: var(--bs-body-bg);
  border-radius: $border-radius;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: var(--bs-body-color);
  cursor: pointer;
  transition: all 0.2s;
}

.filter-tab:hover {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
}

.filter-tab.active {
  background: var(--bs-primary);
  border-color: var(--bs-primary);
  color: white;
}

.filter-tab .badge {
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: $border-radius-pill;
}

.filter-tab.active .badge {
  background: rgba(255, 255, 255, 0.2);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1rem;
}
</style>
